import { z } from 'zod';
import {
  AUDIENCE_CONDITION_TYPES,
  audienceSchema,
  type Audience,
  type AudienceConditionType,
} from './outreach-campaign';

/**
 * What the condition picker renders for a chosen audience.
 *
 * **The ranges are read out of `audienceSchema`, never restated.** A parameter
 * form that says "1–365" while the schema accepts 1–30 produces a campaign the
 * author cannot publish and cannot see why; a form that allows more than the
 * schema does turns a typo into a 422 from a screen that looked valid. Deriving
 * the bounds from the same object that validates the request removes the class
 * of bug rather than asking two files to stay in step.
 *
 * What the schema genuinely cannot express — what to CALL a parameter, and what
 * a sensible starting value is — lives in {@link PARAM_COPY} below. Adding a
 * parameter therefore means editing `audienceSchema` and one entry here, both in
 * this folder, and nothing in the web app.
 */

export interface AudienceNumberField {
  key: string;
  kind: 'number';
  min: number;
  max: number;
  /** Where the picker starts, clamped into `[min, max]`. */
  initial: number;
  label: string;
  /** Rendered after the input: `days`, `months`. */
  unit: string;
}

export interface AudienceToggleField {
  key: string;
  kind: 'toggle';
  /** The schema's own default — the conservative reading of the audience. */
  initial: boolean;
  label: string;
  help: string;
}

export type AudienceParamField = AudienceNumberField | AudienceToggleField;

/** Human names for the catalog entries, for the picker and the editor header. */
export const AUDIENCE_CONDITION_LABELS: Record<AudienceConditionType, string> = {
  new_patients: 'New patients',
  open_referral: 'Open referrals',
  lapsed_patients: 'Lapsed patients',
  upcoming_appointment: 'Upcoming appointment',
  post_visit_followup: 'After a visit',
  no_show_not_rebooked: 'No-shows not rebooked',
};

/**
 * Copy for each parameter, keyed by parameter name.
 *
 * Keys are unique across the whole catalog, which is what lets this be one flat
 * table rather than one per condition. Two conditions that genuinely mean the
 * same parameter should share the key and this entry.
 *
 * `initial` is a starting value, not a default — the schema has no default for
 * the numeric parameters on purpose, because there is no safe number to assume
 * on someone's behalf. It is clamped to the schema's range before use, so a
 * later tightening of a bound cannot leave a stale value here unpublishable.
 */
const PARAM_COPY: Record<
  string,
  { label: string; unit?: string; help?: string; initial?: number }
> = {
  registeredWithinDays: { label: 'Registered within', unit: 'days', initial: 30 },
  openForAtLeastDays: { label: 'Open for at least', unit: 'days', initial: 7 },
  excludeIfBooked: {
    label: 'Drop the referral once something is booked',
    help: 'Off keeps the referral in the audience even after an appointment exists — for a campaign chasing paperwork or benefits rather than a date.',
  },
  noVisitForMonths: { label: 'No visit for', unit: 'months', initial: 6 },
  includeAlreadyBooked: {
    label: 'Include patients who already have something booked',
    help: 'On when the campaign is not asking them to come back — a benefits check or a records request is still relevant.',
  },
  daysAhead: { label: 'Appointment within the next', unit: 'days', initial: 2 },
  includeConfirmed: {
    label: 'Include appointments already confirmed',
    help: 'On for a pre-visit call — intake, insurance, directions — which is just as relevant to a confirmed appointment.',
  },
  daysAfterVisit: { label: 'Days after the visit', unit: 'days', initial: 7 },
  withinDays: { label: 'Missed an appointment within', unit: 'days', initial: 30 },
};

/** The `params` object schema for one condition, straight off the union. */
function paramsShapeFor(type: AudienceConditionType): Record<string, z.ZodType> {
  for (const option of audienceSchema.options) {
    const shape = option.shape;
    const discriminant = shape.type;
    if (discriminant instanceof z.ZodLiteral && discriminant.value === type) {
      const params = shape.params;
      if (params instanceof z.ZodObject) return params.shape as Record<string, z.ZodType>;
    }
  }
  // Unreachable: the union is keyed by the same values as `AudienceConditionType`.
  throw new Error(`No audience condition named "${type}"`);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * The parameters a condition takes, in declaration order, ready to render.
 *
 * Throws for a type outside the catalog rather than returning an empty list — an
 * unknown condition means the frontend and the schema have diverged, and a
 * silently empty parameter form would publish a campaign with the wrong audience.
 */
export function audienceParamFields(type: AudienceConditionType): AudienceParamField[] {
  const shape = paramsShapeFor(type);

  return Object.entries(shape).map(([key, schema]) => {
    const copy = PARAM_COPY[key] ?? { label: key };

    if (schema instanceof z.ZodNumber) {
      // `.min()`/`.max()` are always present on these — every numeric parameter
      // in the catalog is range-bounded (N2). The fallbacks keep a new unbounded
      // parameter renderable instead of crashing the editor.
      const min = schema.minValue ?? 1;
      const max = schema.maxValue ?? 365;
      return {
        key,
        kind: 'number',
        min,
        max,
        initial: clamp(copy.initial ?? min, min, max),
        label: copy.label,
        unit: copy.unit ?? '',
      };
    }

    if (schema instanceof z.ZodDefault && schema.def.innerType instanceof z.ZodBoolean) {
      return {
        key,
        kind: 'toggle',
        initial: schema.def.defaultValue === true,
        label: copy.label,
        help: copy.help ?? '',
      };
    }

    throw new Error(`Audience parameter "${key}" has no editor: unsupported schema`);
  });
}

/**
 * A valid audience of the given type, with every parameter at its starting
 * value and no narrowing at all.
 *
 * Used when the picker switches type: the previous type's parameters are
 * meaningless under the new one, and carrying them over would either fail
 * validation or — worse — silently coincide with a key that means something
 * else.
 */
export function defaultAudience(type: AudienceConditionType): Audience {
  const params: Record<string, number | boolean> = {};
  for (const field of audienceParamFields(type)) {
    params[field.key] = field.initial;
  }
  return audienceSchema.parse({ type, params, filters: [] });
}

/** Every catalog entry with its label, in catalog order. */
export function audienceConditionOptions(): Array<{
  type: AudienceConditionType;
  label: string;
}> {
  return AUDIENCE_CONDITION_TYPES.map((type) => ({
    type,
    label: AUDIENCE_CONDITION_LABELS[type],
  }));
}
